import React, { useState, useEffect } from 'react';
import { Box, TextField, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

interface MapEditorRowProps {
  entryKey: string;
  value: string;
  onKeyChange: (newKey: string) => void;
  onValueChange: (newValue: string) => void;
  onDelete: () => void;
  keyError?: boolean;
}

export const MapEditorRow: React.FC<MapEditorRowProps> = ({
  entryKey,
  value,
  onKeyChange,
  onValueChange,
  onDelete,
  keyError = false,
}) => {
  const [localKey, setLocalKey] = useState(entryKey);

  // Sync local state when prop changes (e.g., when key is actually updated)
  useEffect(() => {
    setLocalKey(entryKey);
  }, [entryKey]);

  const handleKeyBlur = () => {
    // Only commit the change if the key is not empty
    if (localKey.trim() && localKey !== entryKey) {
      onKeyChange(localKey);
    } else if (!localKey.trim()) {
      // Reset to original key if user left it empty
      setLocalKey(entryKey);
    }
  };

  const handleKeyKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.currentTarget as HTMLElement).blur();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        mb: 2,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          label="Key"
          value={localKey}
          onChange={(e) => setLocalKey(e.target.value)}
          onBlur={handleKeyBlur}
          onKeyDown={handleKeyKeyDown}
          size="small"
          fullWidth
          error={keyError}
          helperText={keyError ? 'Key cannot be empty' : ''}
        />
        <IconButton
          onClick={onDelete}
          size="small"
          sx={{ mt: 0.5 }}
          aria-label="delete"
        >
          <DeleteIcon />
        </IconButton>
      </Box>
      <TextField
        label="Value"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        multiline
        rows={3}
        size="small"
        fullWidth
      />
    </Box>
  );
};
